import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { N58CasoAbierto, N58Service } from '../../../services/n58.service';

/**
 * Los casos de pago N58 que esperan una decision, y las tres formas de
 * cerrarlos.
 *
 * Existe porque hasta ahora no habia ninguna: los endpoints estaban hechos y
 * nadie los llamaba, asi que cada caso se cerraba escribiendo SQL contra
 * produccion. Las cuatro reversas de septiembre se hicieron asi.
 *
 * **El modal no decide nada.** Quien decide si un caso se puede reversar es el
 * backend, que manda `reversable` y `motivoNoReversable` ya resueltos; aca solo
 * se pintan. Duplicar esa logica en el front seria pedir que las dos se
 * desincronicen justo cuando hay plata de por medio.
 */
@Component({
  selector: 'app-n58-casos-modal',
  templateUrl: './n58-casos-modal.component.html',
})
export class N58CasosModalComponent implements OnInit {
  casos: N58CasoAbierto[] = [];
  cargando = true;
  error: string | null = null;

  /** Id del caso que esta procesando una accion, para apagar solo esa fila. */
  procesando: number | null = null;

  /** Queda en true si se cerro al menos un caso: el home recarga el contador. */
  private huboCambios = false;

  constructor(
    private n58: N58Service,
    public dialogRef: MatDialogRef<N58CasosModalComponent>,
  ) {}

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    this.cargando = true;
    this.error = null;
    this.n58.listarCasos('ABIERTO').subscribe({
      next: (res) => {
        this.casos = res?.data ?? [];
        this.cargando = false;
      },
      error: (err) => {
        this.error =
          err?.message || 'No se pudieron cargar los casos. Reintentá.';
        this.cargando = false;
      },
    });
  }

  formatearMonto(valor: string | number | null, moneda: string): string {
    if (valor === null || valor === undefined) return '-';
    const n = Number(valor);
    if (Number.isNaN(n)) return '-';
    return `${new Intl.NumberFormat('es-CL').format(n)} ${moneda}`;
  }

  /**
   * Que decirle al operador de cada tipo de caso, y con que color.
   *
   * Antes esto era una sola pregunta —¿es reintegro?— y todo lo demas se
   * titulaba «El pago no aparece en la cartola». Con el vigilante nuevo eso es
   * **falso en las dos direcciones**: en `SIN_WEBHOOK` el pago si esta en la
   * cartola y lo que falta es la confirmacion del banco; en `RECHAZO_BANCO` el
   * banco ya dijo que lo rechazo. El operador decide una reversa leyendo este
   * titulo, asi que un titulo que miente es plata mal devuelta.
   *
   * El color separa lo probado de lo sospechado: `SIN_WEBHOOK` va en ambar
   * porque es la unica senal que se reclasifica sola cuando aparece la
   * evidencia. Los otros tres describen algo que ya pasó.
   */
  private static readonly CASOS: Record<
    N58CasoAbierto['tipo'],
    { titulo: string; fondo: string; texto: string }
  > = {
    SIN_RESPALDO: {
      titulo: 'El pago no aparece en la cartola',
      fondo: '#fef2f2',
      texto: '#b91c1c',
    },
    REINTEGRO: {
      titulo: 'El banco devolvió el pago',
      fondo: '#fff7ed',
      texto: '#c2410c',
    },
    SIN_WEBHOOK: {
      titulo: 'El banco no confirmó el pago',
      fondo: '#fefce8',
      texto: '#854d0e',
    },
    RECHAZO_BANCO: {
      titulo: 'El banco declaró el pago rechazado',
      fondo: '#fef2f2',
      texto: '#b91c1c',
    },
  };

  /**
   * El tipo llega del backend, asi que puede ser uno que esta version no
   * conozca. En ese caso se muestra el tipo crudo en gris: feo pero honesto,
   * que es justo lo que no pasaba antes.
   */
  private estiloDe(caso: N58CasoAbierto) {
    // Un SIN_RESPALDO no se reclasifica nunca, asi que cuando la cartola se
    // pone al dia el tipo sigue diciendo que el pago no esta. El 01/10 fueron
    // 11 casos titulados asi con el renglon ya en la cartola. El titulo sale
    // del dato, no del tipo.
    if (caso.tipo === 'SIN_RESPALDO' && caso.tieneRenglon) {
      return {
        titulo: 'El pago apareció tarde en la cartola',
        fondo: '#fefce8',
        texto: '#854d0e',
      };
    }
    return (
      N58CasosModalComponent.CASOS[caso.tipo] ?? {
        titulo: caso.tipo,
        fondo: '#f3f4f6',
        texto: '#374151',
      }
    );
  }

  tituloDe(caso: N58CasoAbierto): string {
    return this.estiloDe(caso).titulo;
  }

  fondoDe(caso: N58CasoAbierto): string {
    return this.estiloDe(caso).fondo;
  }

  colorDe(caso: N58CasoAbierto): string {
    return this.estiloDe(caso).texto;
  }

  /**
   * Devolverle el saldo al cliente.
   *
   * La confirmacion no es tramite: el 14/09 se reverso una transaccion que ya
   * estaba pagada a mano desde el banco y se pagaron ~220.000 Bs dos veces. Por
   * eso el texto pregunta por el beneficiario, no por la transaccion.
   */
  async reversar(caso: N58CasoAbierto): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: '¿Devolverle el saldo al cliente?',
      html:
        `<p style="text-align:left">Confirmá con el banco que <strong>${caso.beneficiario ?? 'el beneficiario'}</strong> ` +
        `<strong>NO</strong> recibió el pago.</p>` +
        `<p style="text-align:left">Si ya cobró, esto le devuelve la plata al cliente igual y ` +
        `terminamos pagando dos veces.</p>` +
        `<p style="text-align:left">Se le devuelven <strong>${this.formatearMonto(caso.montoOrigen, caso.monedaOrigen ?? '')}</strong> ` +
        `a ${caso.cliente ?? 'el cliente'}.</p>`,
      input: 'text',
      inputPlaceholder: 'Nota (opcional): con quién confirmaste',
      showCancelButton: true,
      confirmButtonText: 'Sí, confirmé con el banco',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b91c1c',
      cancelButtonColor: '#6b7280',
    });
    if (!r.isConfirmed) return;

    this.ejecutar(
      caso,
      this.n58.reversarCaso(caso.id, caso.usuarioId, r.value || 'Reversado desde el panel'),
      'Reversa emitida',
      'El estado y el abono los escribe la lambda en unos segundos.',
    );
  }

  async repuestoAMano(caso: N58CasoAbierto): Promise<void> {
    const r = await Swal.fire({
      icon: 'question',
      title: '¿Ya le pagaste al beneficiario?',
      html:
        `<p style="text-align:left">Esto cierra el caso <strong>sin mover plata</strong>: la transacción ` +
        `queda como enviada porque el cliente sí recibió el servicio.</p>`,
      input: 'text',
      inputPlaceholder: 'Nota (opcional): referencia del pago manual',
      showCancelButton: true,
      confirmButtonText: 'Sí, ya le pagué',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6b7280',
    });
    if (!r.isConfirmed) return;

    this.ejecutar(
      caso,
      this.n58.marcarRepuestoAMano(caso.id, r.value || 'Confirmado desde el panel'),
      'Caso cerrado',
      'Queda registrado que el beneficiario cobró.',
    );
  }

  /** Descartar exige motivo: es la unica salida que no deja rastro de plata. */
  async descartar(caso: N58CasoAbierto): Promise<void> {
    const r = await Swal.fire({
      icon: 'question',
      title: '¿Descartar el caso?',
      text: 'Usalo sólo si fue una falsa alarma. Hay que decir por qué.',
      input: 'text',
      inputPlaceholder: 'Motivo (obligatorio)',
      inputValidator: (v) => (v && v.trim() ? null : 'Escribí el motivo'),
      showCancelButton: true,
      confirmButtonText: 'Descartar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6b7280',
    });
    if (!r.isConfirmed) return;

    this.ejecutar(
      caso,
      this.n58.descartarCaso(caso.id, r.value),
      'Caso descartado',
      'Se guardó el motivo.',
    );
  }

  private ejecutar(
    caso: N58CasoAbierto,
    peticion: { subscribe: (o: any) => void },
    titulo: string,
    detalle: string,
  ): void {
    this.procesando = caso.id;
    peticion.subscribe({
      next: () => {
        this.procesando = null;
        this.huboCambios = true;
        Swal.fire({
          icon: 'success',
          title: titulo,
          text: detalle,
          confirmButtonColor: '#1C593D',
        });
        this.cargar();
      },
      error: (err: any) => {
        this.procesando = null;
        Swal.fire({
          icon: 'error',
          title: 'No se pudo',
          text:
            err?.error?.message ||
            err?.message ||
            'El backend rechazó la operación.',
          confirmButtonColor: '#1C593D',
        });
      },
    });
  }

  cerrar(): void {
    this.dialogRef.close(this.huboCambios);
  }
}
